"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.INVOICE_EDITABLE_KEYS = void 0;
exports.isEditableInvoiceKey = isEditableInvoiceKey;
exports.invoiceEditableKeysAreKnownPaths = invoiceEditableKeysAreKnownPaths;
const invoice_variable_namespace_1 = require("./invoice-variable-namespace");
// The server-side editable-field partition for issued invoices.
//
// This is the security boundary: at the review step an operator may only edit a
// small, non-contract set of fields on the assembled invoice; every other
// render-context value (counterparty, material, quantity, price, entity, legal
// number, all totals, …) is trade data corrected upstream in Foundry, never on
// the document. The UI renders sections from this partition, but the UI is NOT
// the boundary — the override endpoint rejects any key outside the allow-set
// even when the client is bypassed.
//
// Deliberately an explicit **allow-set** (default-deny), not a deny-set like the
// BL-order `isEditableTemplateKey`. The invoice read-only set is the sensitive
// majority, so a new render variable is locked by default and only becomes
// editable by a considered addition here.
//
// The **bank** is editable too, but as a *constrained* selection — it is stored
// as the `invoice.issuer_bank_id` FK (validated against the entity's bank
// catalog), not as a free `field_overrides` value — so it is intentionally NOT
// in this key set. See the invoice review endpoint for the bank rule.
exports.INVOICE_EDITABLE_KEYS = new Set([
    // Material description — China/regional lingo the trader may not have keyed
    // correctly on the SO/PO; the material `code`/grade stays read-only.
    'description_of_goods',
    // Counterparty VAT id — a correctable free field (the counterparty identity
    // itself is read-only). The pre-generation validator also reads it from the
    // primary `Company_address`.
    'counterparty_vat_id',
    // Tax-line text — the reverse-charge / tax-exempt wording. Editable on the
    // advance kind today (its template binds `{{vat_note_html}}`); the render
    // wiring on the other kinds + the default wording is a finance/legal call.
    'vat_note_html',
    // Operator-supplied delivery / origin / contract detail with no upstream
    // source yet — the collapsed BL body renders each as an em-dash until the
    // operator fills it here. These are descriptive shipment/commercial fields,
    // not trade data: none feed a total, a legal number, or the counterparty
    // identity, so an operator edit cannot alter the invoice's monetary or legal
    // substance. Confirmed editable with finance (2026-07-08). The broken-out
    // pricing formula (base price, payable factor, premium/discount, QP dates,
    // worked-calc line) is deliberately NOT here — it is derived trade data,
    // corrected upstream, and stays read-only.
    'packing',
    'transport_mode',
    'vessel_voyage',
    'eta',
    'country_of_origin',
    'contract_term_line',
    'weight_tolerance_line',
]);
// True when a key may be written through the invoice review override endpoint.
function isEditableInvoiceKey(key) {
    return exports.INVOICE_EDITABLE_KEYS.has(key);
}
// Every editable key must be a real invoice render-context variable — a typo
// here would silently accept an override that never reaches the document. This
// invariant is asserted in the unit test; exported so callers can reuse it.
function invoiceEditableKeysAreKnownPaths() {
    const known = new Set(invoice_variable_namespace_1.ALL_INVOICE_VARIABLE_PATHS);
    for (const key of exports.INVOICE_EDITABLE_KEYS) {
        if (!known.has(key))
            return false;
    }
    return true;
}
//# sourceMappingURL=invoice-editable-fields.js.map